/* ==========================================================================
   LONGLIFE LANDING — behaviour for the `landing-*` sections.

   Two constraints shape this file:

   1. It is loaded by every landing section (via snippets/lb-assets.liquid),
      so the same <script src> appears several times on the page. The browser
      fetches once but executes once per tag, so every routine below claims
      the element it binds to and refuses to bind twice.

   2. In the theme editor, Shopify swaps a section's markup in place and fires
      `shopify:section:load`. External <script> tags in the replaced HTML do
      not re-execute, so the listener at the bottom re-runs init() instead.
      That is why init() has to be safe to call any number of times.
   ========================================================================== */
(function () {
  'use strict';

  /* Marks an element as handled. Returns false if someone already claimed it,
     which is what makes every init below re-entrant. */
  function claim(el, key) {
    if (!el || el.dataset[key] === 'true') return false;
    el.dataset[key] = 'true';
    return true;
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function init() {
    initCountdown();
    initReveals();
    initPulse();
    initSticky();
    initBundlePricing();
  }

  /* ========================================================================
     OFFER COUNTDOWN — end date comes from the section setting via data-end.
     Hides itself for good once expired, and stays hidden when the merchant
     unticks "Show offer bar" (data-enabled="false").
     ==================================================================== */
  function initCountdown() {
    var bar = document.getElementById('lb-offer-bar');
    if (!bar || !claim(bar, 'lbCountdown')) return;

    /* The merchant's checkbox wins over everything below. Without this the
       tick() call would clear the `hidden` attribute Liquid rendered and the
       bar would come back on its own. */
    if (bar.dataset.enabled === 'false') {
      bar.hidden = true;
      return;
    }

    var clock = document.getElementById('lb-clock');
    var endMs = new Date(bar.dataset.end).getTime();

    /* An unparseable or past date means no offer, not a broken clock. */
    if (isNaN(endMs)) {
      bar.hidden = true;
      return;
    }

    function pad(n) { return String(n).padStart(2, '0'); }

    function tick() {
      var left = endMs - Date.now();
      if (left <= 0) { bar.hidden = true; return; }
      bar.hidden = false;
      var s = Math.floor(left / 1000);
      if (clock) {
        clock.textContent =
          pad(Math.floor(s / 86400)) + 'd ' + pad(Math.floor((s % 86400) / 3600)) + 'h ' +
          pad(Math.floor((s % 3600) / 60)) + 'm ' + pad(s % 60) + 's';
      }
      window.setTimeout(tick, 1000);
    }
    tick();
  }

  /* ========================================================================
     SCROLL REVEALS
     ==================================================================== */
  function initReveals() {
    /* `.ll-reveal` is the home and contact pages, `.lb-reveal` the product
       landing. Same behaviour, same guarantees — worth one implementation
       rather than two that drift apart. */
    var targets = Array.prototype.filter.call(
      document.querySelectorAll('.lb-reveal, .ll-reveal'),
      function (el) { return claim(el, 'lbReveal'); }
    );
    if (!targets.length) return;

    /* Reduced motion, or no IntersectionObserver: show everything at once.
       The CSS starts .lb-reveal at opacity 0, so failing to reach this line
       would leave the page blank — never gate it behind anything optional. */
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.setAttribute('data-in', 'true'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { e.target.setAttribute('data-in', 'true'); io.unobserve(e.target); }
      });
    }, { threshold: .18, rootMargin: '0px 0px -8% 0px' });
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ========================================================================
     HEARTBEAT SIGNATURE — measures the real path length once, then loops
     a short lit segment along it. Pauses while off screen.
     ==================================================================== */
  function initPulse() {
    var pulseWrap = document.getElementById('lb-pulse');
    var pulsePath = document.getElementById('lb-pulsePath');
    if (prefersReducedMotion() || !pulseWrap || !pulsePath) return;
    if (typeof pulsePath.getTotalLength !== 'function' || typeof pulsePath.animate !== 'function') return;
    if (!claim(pulseWrap, 'lbPulse')) return;

    var len = Math.ceil(pulsePath.getTotalLength());
    var pulseBase = pulseWrap.querySelector('.lb-pulse-base');
    var pulseScan = pulseWrap.querySelector('.lb-pulse-scan');
    var pulseAnims = [];

    pulseBase.style.opacity = '.4';
    pulseBase.style.strokeDasharray = len;
    pulseAnims.push(pulseBase.animate(
      [{ strokeDashoffset: len }, { strokeDashoffset: 0 }],
      { duration: 2400, delay: 350, easing: 'cubic-bezier(.16,1,.3,1)', fill: 'both' }
    ));

    pulseScan.style.display = 'block';
    pulseScan.style.strokeDasharray = Math.round(len * 0.12) + ' ' + Math.round(len * 0.88);
    pulseAnims.push(pulseScan.animate(
      [{ strokeDashoffset: 0 }, { strokeDashoffset: -len }],
      { duration: 3800, delay: 2100, iterations: Infinity, easing: 'linear' }
    ));

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (e) {
        pulseAnims.forEach(function (a) { e[0].isIntersecting ? a.play() : a.pause(); });
      }, { threshold: 0 }).observe(pulseWrap);
    }
  }

  /* ========================================================================
     STICKY CTA

     Visible from the first paint everywhere — it no longer waits for the hero
     to scroll past, which is the behaviour that was asked for.

     The one exception is narrow screens while the buy block is on screen. On a
     phone the bar and the real add-to-cart button end up in the same few
     hundred pixels, and a permanent bar sits on top of the button it is meant
     to lead to. Desktop has room for both, so nothing hides there.

     The 719px breakpoint is the same one landing-bowl.css uses to strip the
     bar down on mobile; keep the two in step if either moves.

     Because the bar is `position: fixed`, it permanently covers the bottom of
     the viewport, so landing-bowl.css reserves matching bottom padding —
     otherwise the footer's last row would sit underneath it, unreachable.
     ==================================================================== */
  function initSticky() {
    var sticky = document.getElementById('lb-sticky');
    if (!sticky || !claim(sticky, 'lbSticky')) return;
    sticky.setAttribute('data-shown', 'true');

    /* Publish the measured height so the CSS can reserve exactly that much at
       the foot of the page. Measured rather than hard-coded because the bar
       grows when the product title wraps, which it does on narrow screens. */
    function publishHeight() {
      var h = Math.ceil(sticky.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--lb-sticky-h', h + 'px');
    }
    publishHeight();

    if ('ResizeObserver' in window) {
      new ResizeObserver(publishHeight).observe(sticky);
    } else {
      window.addEventListener('resize', publishHeight);
    }

    var buy = document.getElementById('lb-buy');
    if (!buy || !('IntersectionObserver' in window) || !window.matchMedia) return;

    var narrow = window.matchMedia('(max-width: 719px)');
    var overBuy = false;

    function sync() {
      sticky.setAttribute('data-shown', (narrow.matches && overBuy) ? 'false' : 'true');
    }

    new IntersectionObserver(function (entries) {
      overBuy = entries[0].isIntersecting;
      sync();
    }, { threshold: 0.12 }).observe(buy);

    /* Rotating a phone or resizing across the breakpoint has to re-evaluate,
       otherwise the bar stays hidden on a desktop-width viewport. */
    if (narrow.addEventListener) narrow.addEventListener('change', sync);
    else if (narrow.addListener) narrow.addListener(sync);
  }

  /* ========================================================================
     BUNDLE PRICING — every radio carries pre-formatted totals rendered by
     Liquid from the product's real price (data-total / data-was, already
     through the `money` filter), so this only ever swaps text, never does
     currency math or formatting itself. That is what keeps it correct in
     every currency and market the store sells in.
     ==================================================================== */
  function initBundlePricing() {
    var form = document.getElementById('lb-buy-form');
    if (!form || !claim(form, 'lbBundle')) return;

    var total = document.getElementById('lb-total');
    var totalWas = document.getElementById('lb-total-was');
    var qtyInput = form.querySelector('.lb-qty-input');
    var variantInput = form.querySelector('.product-variant-id');

    /* Per-variant prices, pre-formatted by Liquid. Absent on single-variant
       products, in which case the radios' own data-total/data-was are used. */
    var priceData = null;
    var dataEl = form.querySelector('.lb-variant-data');
    if (dataEl) {
      try { priceData = JSON.parse(dataEl.textContent); } catch (e) { priceData = null; }
    }

    function currentVariantId() {
      var picked = form.querySelector('input[name="lb-variant"]:checked');
      return picked ? picked.value : (variantInput ? variantInput.value : null);
    }

    /* Inactive slides are `display:none`, and a lazy image inside one is never
       fetched — switching colour would reveal an empty frame while the browser
       started downloading. So the first time anyone touches the picker, every
       variant image is flipped to eager: the one being selected loads now, and
       the remaining colours are warm for the clicks that usually follow.
       Deferred until that first interaction so visitors who never open the
       picker are not made to download six photos. */
    var warmed = false;
    function warmVariantImages() {
      if (warmed) return;
      warmed = true;
      form.querySelectorAll('input[name="lb-variant"]').forEach(function (radio) {
        if (!radio.dataset.media) return;
        var item = document.querySelector('#lb-buy [data-media-id="' + radio.dataset.media + '"]');
        var img = item && item.querySelector('img[loading="lazy"]');
        if (img) img.loading = 'eager';
      });
    }

    /* Colour change: point the cart at the new variant, swap the gallery to
       that variant's shot, then re-price. Dawn's gallery exposes
       setActiveMedia(mediaId, prepend) — the same call its own variant picker
       makes, so the thumbnails and the zoom lightbox stay in step. */
    function onVariantChange(picked) {
      if (variantInput) variantInput.value = picked.value;

      warmVariantImages();

      var mediaId = picked.dataset.media;
      if (mediaId) {
        var gallery = document.querySelector('#lb-buy media-gallery');
        if (gallery && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(mediaId, true);
        }
      }
      sync();
    }

    function sync() {
      var checked = form.querySelector('input[name="lb-bundle"]:checked');
      if (!checked) return;

      var amount = checked.dataset.total || '';
      var was = checked.dataset.was || '';

      /* Prefer the per-variant table when the product has variants. */
      var vid = currentVariantId();
      if (priceData && vid && priceData[vid]) {
        var v = priceData[vid];
        var qty = checked.value;
        if (v.totals && v.totals[qty] != null) amount = v.totals[qty];
        if (v.was && v.was[qty] != null) was = v.was[qty];

        /* Per-unit label on every bundle row follows the selected variant. */
        if (v.unit) {
          form.querySelectorAll('.lb-unit-price').forEach(function (el) {
            el.textContent = v.unit;
          });
        }
      }

      if (total) total.textContent = amount;
      if (totalWas) { totalWas.textContent = was; totalWas.hidden = !was; }

      /* The sticky bar is a separate section and may have been re-rendered
         since load, so it is looked up on every sync rather than cached. */
      var stickyPrice = document.getElementById('lb-sticky-price');
      var stickyWas = document.getElementById('lb-sticky-was');
      var stickySel = document.getElementById('lb-sticky-sel');
      if (stickyPrice) stickyPrice.textContent = amount;
      if (stickyWas) { stickyWas.textContent = was; stickyWas.hidden = !was; }
      if (stickySel) stickySel.textContent = checked.dataset.label || '';

      /* This is the only thing the cart actually reads. */
      if (qtyInput) qtyInput.value = checked.value;
    }

    form.addEventListener('change', function (evt) {
      if (evt.target.name === 'lb-bundle') sync();
      if (evt.target.name === 'lb-variant') onVariantChange(evt.target);
    });
    sync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-bind whatever the merchant just edited. */
  document.addEventListener('shopify:section:load', init);
})();
